// Healther OPD — SPA entry. Replaces the legacy Babel-in-browser pages.
// Self-hosted @font-face declarations FIRST — index.html used to <link> Inter +
// Noto Sans Devanagari from the Google Fonts CDN, a network dependency at first
// paint that an offline clinic tablet could never satisfy (bug B5). One file
// owns every face now; see styles/fonts.css.
import './styles/fonts.css';
// shadcn/ui + Tailwind v4 pipeline FIRST so the legacy sheets below cascade
// last and retain priority on any shared selector (the 69 legacy screens must
// render unchanged — see src/styles/shadcn.css header).
import './styles/shadcn.css';
import './styles/opd-ui.css';
import './styles/colors_and_type.css';
// Design-language v2 skin — every rule scoped under html[data-ui="v2"], so
// classic stays byte-identical. The data-ui attribute is stamped by
// lib/ui-language.js at import (before first render).
import './styles/ui-v2.css';
import './lib/ui-language.js';
// Classic scripts ported as side-effect imports — they attach the globals the
// screens were written against (OPDTheme, HealtherUI, OPDVoice and kiosk guide
// illustrations).
import './lib/theme.js';
import './lib/healther-ui.js';
import './lib/opd-voice.js';
import './lib/guide-illustrations.js';

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
// Self-hosted OTA (over-the-air) tablet updates — native-only, no-op on web.
// Kicks the rollback heartbeat + checks our own /ota channel on boot/resume.
import { startOtaUpdates } from './lib/ota.js';

// Capgo 8.50.2 requires this at entry startup, before config/network work. It
// only acknowledges that the downloaded JavaScript bundle initialized; update
// discovery remains deferred and never blocks first paint.
startOtaUpdates();

// Legacy screens reference the React global (React.useState etc.) — keep it
// available while ported modules are migrated incrementally.
window.React = React;

// Build identity (owner 2026-08-11) — one boot log + a window global, so any
// device or console can answer "which build is this?" without guessing. The
// same stamp shows on the login footer and in the avatar menu.
import('./lib/build-info.js').then(({ buildInfo, buildLabel }) => {
  try {
    window.__HEALTHER_BUILD = buildInfo();
    console.info(`[healther] ${buildLabel()}`);
  } catch (_) { /* never block boot on the stamp */ }
});

// healther-ui.js auto-renders its own floating EN/हिं pill (top-right) — in
// the SPA every screen listens to the bridge's 'opd:lang' toggle instead, so
// that second pill is a confusing duplicate. Neutralize it.
if (window.HealtherUI) {
  window.HealtherUI.renderLangToggle = () => {};
  document.getElementById('healther-lang-toggle')?.remove();
}

// The audio-upload service worker was DELETED in the 2026-07-14 QA remediation
// (3169e05), but browsers that registered it earlier keep re-requesting
// /audio-upload-sw.js on every navigation forever (a stale registration's
// update check — seen 404-spamming the demo logs 2026-08-04). Unregister THAT
// worker only; never touch other registrations (Capacitor/native shells).
if (typeof navigator !== 'undefined' && navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => {
      for (const reg of regs) {
        const url = (reg.active || reg.waiting || reg.installing)?.scriptURL || '';
        if (url.includes('audio-upload-sw')) reg.unregister();
      }
    })
    .catch(() => { /* SW API unavailable (insecure context) — nothing to clean */ });
}

ReactDOM.createRoot(document.getElementById('app')).render(<App />);
